import Animation from "@/components/Animation";
import { LessonDataI } from "@/components/quiz/typesAndEnums";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { TabsContent } from "@/components/ui/tabs";
import { LessonScreenContentI } from "@/hooks/types";
import { CheckCircle, Download, FileText, Play } from "lucide-react";

const LessonTabsContent = ({
  lessonData,
  setScreenContent,
}: {
  lessonData: LessonDataI;
  setScreenContent: ({
    elementId,
    screen,
  }: {
    elementId: string;
    screen: LessonScreenContentI;
  }) => void;
}) => {
  return (
    <>
      <TabsContent value={LessonScreenContentI.QUIZ} className="space-y-4">
        <Animation>
          <div className="flex flex-col gap-4">
            {lessonData?.quiz_id ? (
              [
                {
                  quiz_id: lessonData.quiz_id,
                  quiz_duration: lessonData.quiz_duration,
                  quiz_title: lessonData.quiz_title,
                  quiz_questions: lessonData.quiz_questions,
                  quiz_required: lessonData.quiz_required,
                  quiz_trials: lessonData.quiz_trials,
                  past_quiz_trials: lessonData.past_quiz_trials,
                },
              ].map((quiz) => (
                <Card
                  key={quiz.quiz_id}
                  className="hover:shadow-md transition-shadow"
                >
                  <CardContent className="md:p-6">
                    <div className="flex flex-col gap-4 md:flex-row items-end md:items-center justify-between">
                      <Button
                        className="cursor-pointer order-2 md:order-1 self-start"
                        onClick={() =>
                          setScreenContent({
                            elementId: quiz.quiz_id.toString(),
                            screen: LessonScreenContentI.QUIZ,
                          })
                        }
                      >
                        {quiz.past_quiz_trials
                          ? "عرض النتيجة"
                          : "إدخل الإمتحان"}
                      </Button>
                      <div className="flex items-center space-x-4 order-1 md:order-2">
                        <div>
                          <h3 className="font-semibold text-lg text-end">
                            {quiz.quiz_title}
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            <span>الأسئلة: {quiz.quiz_questions.length}</span>
                            <span>الوقت: {quiz.quiz_duration / 60} دقيقة</span>
                            {quiz.past_quiz_trials && (
                              <span className="text-green-600">
                                الدرجة: {quiz.past_quiz_trials[0].grade[0]}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                          {quiz.past_quiz_trials ? (
                            <CheckCircle className="w-6 h-6 text-green-600" />
                          ) : (
                            <FileText className="w-6 h-6 text-green-600" />
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div
                role="alert"
                className="my-5 w-full rounded-lg bg-white border border-slate-300 text-slate-700 px-4 py-3 flex flex-row-reverse"
              >
                لا يوجد إمتحانات
              </div>
            )}
          </div>
        </Animation>
      </TabsContent>

      <TabsContent value={LessonScreenContentI.VIDEO} className="space-y-4">
        <Animation>
          <div className="flex flex-col gap-4">
            {lessonData.lesson_videos && lessonData.lesson_videos.length > 0 ? (
              lessonData.lesson_videos?.map(
                (video: {
                  video_title: string;
                  video_id: string;
                  index: number;
                }) => (
                  <Card
                    key={video.video_id}
                    className="hover:shadow-md transition-shadow"
                  >
                    <CardContent className="md:p-6">
                      <div className="flex flex-col gap-4 md:flex-row items-end md:items-center justify-between">
                        <Button
                          className="cursor-pointer order-2 md:order-1 self-start"
                          onClick={() =>
                            setScreenContent({
                              elementId: video.video_id,
                              screen: LessonScreenContentI.VIDEO,
                            })
                          }
                        >
                          شاهد الفيديو
                        </Button>
                        <div className="flex items-center space-x-4 order-1 md:order-2">
                          <div>
                            <h3 className="font-semibold text-lg">
                              {video.video_title}
                            </h3>
                            {/* <div className="flex items-center text-sm text-gray-600">
                          <Clock className="w-4 h-4 mr-1" />
                          {video.duration}
                          </div> */}
                          </div>
                          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                            {false ? (
                              <CheckCircle className="w-6 h-6 text-green-600" />
                            ) : (
                              <Play className="w-6 h-6 text-blue-600" />
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )
              )
            ) : (
              <div
                role="alert"
                className="my-5 w-full rounded-lg bg-white border border-slate-300 text-slate-700 px-4 py-3 flex flex-row-reverse"
              >
                لا يوجد فيديوهات
              </div>
            )}
          </div>
        </Animation>
      </TabsContent>

      <TabsContent value={LessonScreenContentI.HW} className="space-y-4">
        <Animation>
          <div className="flex flex-col gap-4">
            {lessonData?.hw_id ? (
              [
                {
                  id: lessonData.hw_id,
                  questions: lessonData.hw_questions,
                  raw_data: lessonData.hw_raw_data,
                  result: lessonData.hw_result,
                  title: lessonData.hw_title,
                },
              ].map((hw) => (
                <Card key={hw.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="md:p-6">
                    <div className="flex flex-col md:flex-row gap-4 items-end md:items-center justify-between">
                      <Button
                        className="cursor-pointer self-start order-2 md:order-1"
                        onClick={() =>
                          setScreenContent({
                            elementId: hw.id.toString(),
                            screen: LessonScreenContentI.HW,
                          })
                        }
                      >
                        {hw.result ? "عرض النتيجة" : "إستكمال"}
                      </Button>
                      <div className="flex items-center space-x-4 order-1 md:order-2">
                        <div>
                          <h3 className="font-semibold text-lg text-end">
                            {hw.title}
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            <span>الأسئلة: {hw.questions.length}</span>
                            {hw.result && (
                              <span className="text-green-600">
                                الدرجة: {JSON.parse(hw.result).score}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                          {hw.result ? (
                            <CheckCircle className="w-6 h-6 text-orange-600" />
                          ) : (
                            <FileText className="w-6 h-6 text-orange-600" />
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div
                role="alert"
                className="my-5 w-full rounded-lg bg-white border border-slate-300 text-slate-700 px-4 py-3 flex flex-row-reverse"
              >
                لا يوجد واجبات
              </div>
            )}
          </div>
        </Animation>
      </TabsContent>

      <TabsContent value={LessonScreenContentI.FILES} className="space-y-4">
        <Animation>
          <div className="flex flex-col gap-4">
            {lessonData.lesson_files && lessonData.lesson_files.length > 0 ? (
              lessonData.lesson_files.map((file) => (
                <Card
                  key={file.url}
                  className="hover:shadow-md transition-shadow"
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row gap-4 items-end md:items-center justify-between">
                      <a
                        href={file.url}
                        className="py-2 px-3 flex justify-center items-center bg-primary rounded-lg no-underline text-white text-sm order-2 md:order-1 self-start"
                        target="_blank"
                      >
                        <Download className="size-4 m-0 mr-2" />
                        تحميل
                      </a>
                      <div className="flex items-center space-x-4 order-1 md:order-2">
                        <div>
                          <h3 className="font-semibold text-lg">{file.name}</h3>
                          <p className="text-sm text-end text-gray-600">
                            {`${Math.round(
                              file.size < 1000000
                                ? file.size / 1000
                                : file.size / 1000000
                            )} ${file.size > 1000000 ? "MB" : "KB"}`}
                          </p>
                        </div>
                        <div className="size-12 bg-purple-100 rounded-lg flex items-center justify-center">
                          <FileText className="size-6 text-purple-600" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div
                role="alert"
                className="my-5 w-full rounded-lg bg-white border border-slate-300 text-slate-700 px-4 py-3 flex flex-row-reverse"
              >
                لا يوجد ملفات
              </div>
            )}
          </div>
        </Animation>
      </TabsContent>
    </>
  );
};

export default LessonTabsContent;
